/**
 * Logique pure de l'écran de saisie d'une facture d'achat (EF-0502), sans
 * React ni appel réseau.
 *
 * Elle ne double pas le serveur : c'est lui qui compose la facture, applique
 * le taux en vigueur à la date et dit où va la taxe. Ce qui est ici sert à
 * répondre **pendant la frappe**, avant l'aller-retour — les totaux, l'écart
 * avec le montant annoncé par la facture, la ligne qu'on ajoute ou qu'on
 * retire.
 *
 * Les montants s'additionnent en centimes entiers : additionner des nombres à
 * virgule flottante finit par afficher un total de 1 799,9999999 là où le
 * comptable attend 1 800,00.
 */

export interface LigneDeFacture {
  /** Compte de charge ou d'immobilisation, tel qu'il se tape. */
  compte: string;
  libelle: string;
  /** Base hors taxes, telle qu'elle se tape — virgule ou point. */
  baseHT: string;
  codeDeTva: string;
  /** Taxe de la facture lorsqu'elle corrige l'arrondi ; vide, le calcul fait foi. */
  tva: string;
}

export const ligneVide = (codeDeTva = ""): LigneDeFacture => ({
  compte: "",
  libelle: "",
  baseHT: "",
  codeDeTva,
  tva: "",
});

/**
 * Montant tel qu'il se tape, en centimes.
 *
 * La virgule vaut le point — c'est ainsi qu'un comptable français ou marocain
 * tape un montant —, les espaces sont ignorés, et tout le reste vaut zéro
 * plutôt que « pas un nombre », qui contaminerait chaque total.
 */
export function enCentimes(saisi: string): number {
  const nettoye = saisi.replace(/\s/g, "").replace(",", ".");
  if (nettoye === "" || !/^-?\d*\.?\d*$/.test(nettoye)) return 0;
  const valeur = Number(nettoye);
  return Number.isFinite(valeur) ? Math.round(valeur * 100) : 0;
}

export const enDirhams = (centimes: number): number => centimes / 100;

/** Vrai si la saisie porte quelque chose qui ne se lit pas comme un montant. */
export function montantIllisible(saisi: string): boolean {
  const nettoye = saisi.replace(/\s/g, "").replace(",", ".");
  return nettoye !== "" && !/^-?\d*\.?\d*$/.test(nettoye);
}

/** Taux du barème, tel que l'écran l'a chargé pour la date de la facture. */
export interface TauxConnu {
  code: string;
  pourCent: number;
}

/**
 * Taxe d'une base à un taux, arrondie au centime le plus proche.
 *
 * La règle est celle du domaine : arrondi commercial, le demi s'éloignant de
 * zéro. La reproduire ici est le seul moyen d'afficher un total juste avant
 * que le serveur ait répondu ; c'est lui qui fait foi.
 */
export function taxeEnCentimes(baseEnCentimes: number, pourCent: number): number {
  const brut = (baseEnCentimes * pourCent) / 100;
  return Math.sign(brut) * Math.round(Math.abs(brut));
}

export interface TotauxDeFacture {
  /** Nombre de lignes qui portent une base. */
  nombre: number;
  totalHT: number;
  totalTva: number;
  totalTTC: number;
  /** Vrai si une ligne porte un taux qu'aucun barème chargé ne connaît. */
  tauxInconnu: boolean;
}

/**
 * Totaux de la facture d'après ce qui est tapé.
 *
 * Une ligne dont la taxe est saisie l'emporte sur le calcul : c'est la
 * facture qui fait foi sur son propre centime d'arrondi (RG-14), et le
 * serveur refusera l'écart s'il dépasse l'arrondi admis.
 */
export function totauxDeFacture(
  lignes: readonly LigneDeFacture[],
  taux: readonly TauxConnu[],
): TotauxDeFacture {
  const parCode = new Map(taux.map((t) => [t.code, t.pourCent]));
  let nombre = 0;
  let totalHT = 0;
  let totalTva = 0;
  let tauxInconnu = false;

  for (const ligne of lignes) {
    const base = enCentimes(ligne.baseHT);
    if (base === 0 && ligne.compte.trim() === "") continue;
    nombre += 1;
    totalHT += base;
    if (ligne.codeDeTva === "") continue;
    if (ligne.tva.trim() !== "") {
      totalTva += enCentimes(ligne.tva);
      continue;
    }
    const pourCent = parCode.get(ligne.codeDeTva);
    if (pourCent === undefined) {
      tauxInconnu = true;
      continue;
    }
    totalTva += taxeEnCentimes(base, pourCent);
  }

  return {
    nombre,
    totalHT: enDirhams(totalHT),
    totalTva: enDirhams(totalTva),
    totalTTC: enDirhams(totalHT + totalTva),
    tauxInconnu,
  };
}

/**
 * Écart entre le total annoncé par la facture et la ventilation, en dirhams.
 *
 * Nul quand rien n'est annoncé : on ne signale pas un écart avec une valeur
 * qui n'a pas été saisie.
 */
export function ecartAvecLeTotalAnnonce(totaux: TotauxDeFacture, annonce: string): number {
  if (annonce.trim() === "") return 0;
  return enDirhams(enCentimes(annonce) - Math.round(totaux.totalTTC * 100));
}

/** Vrai si la facture peut être soumise : un fournisseur, un numéro, et au moins une base. */
export function estPrete(
  entete: { fournisseur: string; numero: string; date: string },
  lignes: readonly LigneDeFacture[],
): boolean {
  if (entete.fournisseur.trim() === "" || entete.numero.trim() === "" || entete.date === "") return false;
  return lignes.some((ligne) => ligne.compte.trim() !== "" && enCentimes(ligne.baseHT) > 0);
}

/** Lignes retenues pour l'envoi : celles qui portent un compte et une base. */
export function lignesAEnvoyer(lignes: readonly LigneDeFacture[]) {
  return lignes
    .filter((ligne) => ligne.compte.trim() !== "" && enCentimes(ligne.baseHT) !== 0)
    .map((ligne) => ({
      compte: ligne.compte.trim(),
      libelle: ligne.libelle.trim() === "" ? null : ligne.libelle.trim(),
      baseHT: enDirhams(enCentimes(ligne.baseHT)),
      codeDeTva: ligne.codeDeTva === "" ? null : ligne.codeDeTva,
      tva: ligne.tva.trim() === "" ? null : enDirhams(enCentimes(ligne.tva)),
    }));
}

/**
 * Code d'auxiliaire proposé depuis une raison sociale : les lettres et les
 * chiffres, sans accent, en majuscules.
 *
 * La même règle que le domaine, pour que l'écran montre ce que le serveur
 * retiendrait — sans quoi le code proposé changerait à l'enregistrement.
 */
export function codeProposePour(raisonSociale: string): string {
  // La décomposition NFD sépare la lettre de son accent ; le filtre qui suit
  // ne retient que lettres et chiffres, et emporte l'accent avec le reste.
  return raisonSociale
    .normalize("NFD")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 24);
}
